import React, { useState, useEffect } from 'react';
import TeachBack from './TeachBack';
import ContributeExample from './ContributeExample';
import { trackPauseDuration, trackTeachBack } from '../utils/patternSignals';

/**
 * CompletionRitual - Meaningful level completion
 *
 * Replaces "Level Complete! Next →" with a moment of pause.
 * Acknowledges growth, invites reflection, integrates teach-back.
 *
 * Design principles:
 * - Slow down the rush to "next"
 * - Acknowledge effort and growth
 * - No confetti, no fake celebration
 * - Real recognition of real work
 */
function CompletionRitual({
  levelNumber,
  levelTitle,
  conceptName,
  teachBackPrompt,
  lensCallout,
  onContinue
}) {
  const [showTeachBack, setShowTeachBack] = useState(false);
  const [showContribution, setShowContribution] = useState(false);
  const [startTime] = useState(Date.now());

  // Track pause duration when component unmounts or advances
  useEffect(() => {
    return () => {
      const pauseDuration = Date.now() - startTime;
      trackPauseDuration(levelNumber, pauseDuration);
    };
  }, [levelNumber, startTime]);

  const handleTeachBackComplete = () => {
    trackTeachBack(true);
    setShowTeachBack(true);
    setShowContribution(true);
  };

  const handleTeachBackSkip = () => {
    trackTeachBack(false);
    setShowTeachBack(true);
    setShowContribution(true);
  };

  const handleContributionComplete = () => {
    setShowContribution(false);
    // Continue to next level
    onContinue();
  };

  const handleContributionSkip = () => {
    setShowContribution(false);
    // Continue to next level
    onContinue();
  };

  if (!showTeachBack) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Completion acknowledgment - calm, not hyped */}
          <div className="text-center mb-8">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center">
                <span className="text-4xl">✓</span>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-200 mb-3">
              Level {levelNumber} Complete
            </h2>

            <h3 className="text-xl text-slate-400 mb-6">
              {levelTitle}
            </h3>

            {/* Pause moment - this matters */}
            <div className="max-w-md mx-auto mb-8 p-6 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-slate-300 leading-relaxed mb-4">
                Take a moment.
              </p>
              <p className="text-slate-300 leading-relaxed">
                You now understand {conceptName}.
              </p>
              <p className="text-slate-300 leading-relaxed font-medium mt-4">
                This is power.
              </p>
            </div>

            {/* Lens callout - meaning anchor */}
            {lensCallout && (
              <div className="max-w-lg mx-auto mb-8 p-6 bg-slate-900/50 border-l-4 border-amber-500 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🟡</span>
                  <div>
                    <div className="text-amber-400 text-sm font-medium uppercase tracking-wide mb-2">
                      {lensCallout.pillar.replace('-', ' ')} Literacy
                    </div>
                    <p className="text-slate-300 leading-relaxed">
                      {lensCallout.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Before continuing prompt */}
            <div className="mb-6">
              <p className="text-slate-400 mb-4">
                Before you continue:
              </p>
            </div>
          </div>

          {/* Teach-back integration */}
          <TeachBack
            levelId={levelNumber}
            conceptName={conceptName}
            prompt={teachBackPrompt}
            pillarKey={lensCallout?.pillar}
            onComplete={handleTeachBackComplete}
            onSkip={handleTeachBackSkip}
          />
        </div>
      </div>
    );
  }

  // After teach-back, show contribution form if enabled
  if (showContribution) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="mb-6 text-center">
            <p className="text-slate-300 text-lg">
              Your reflection has been saved
            </p>
          </div>

          <ContributeExample
            levelId={levelNumber}
            pillarKey={lensCallout?.pillar}
            onContribute={handleContributionComplete}
            onSkip={handleContributionSkip}
          />
        </div>
      </div>
    );
  }

  // Final state - should not reach here, but just in case
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <button
          onClick={onContinue}
          className="px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-lg transition"
        >
          Continue to Next Level →
        </button>
      </div>
    </div>
  );
}

export default CompletionRitual;
